import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  clientEventsApi,
  type CreateClientEventInput,
  type UpdateClientEventInput,
} from "../api/clientEvents";
import { qk } from "../../lib/queryKeys";

/**
 * Client + date annotations for the wellbeing dashboards. Scoped to a client and
 * an optional date window; disabled when no client is selected (all-clients view
 * has no single client to annotate).
 */
export const useClientEvents = (params: {
  clientId: string | null;
  startDate?: string;
  endDate?: string;
}) => {
  const { clientId, startDate, endDate } = params;
  return useQuery({
    queryKey: qk.clientEvents(clientId, { startDate, endDate }),
    queryFn: () =>
      clientEventsApi.list({
        client_id: clientId as string,
        start_date: startDate,
        end_date: endDate,
      }),
    enabled: Boolean(clientId),
    staleTime: 60 * 1000,
  });
};

/** Invalidates every event list for a client after a write. */
const useInvalidateClientEvents = () => {
  const queryClient = useQueryClient();
  return (clientId: string | null) =>
    queryClient.invalidateQueries({
      queryKey: ["client-events", clientId],
    });
};

export const useCreateClientEvent = () => {
  const invalidate = useInvalidateClientEvents();
  return useMutation({
    mutationFn: (input: CreateClientEventInput) => clientEventsApi.create(input),
    onSuccess: (_data, input) => invalidate(input.client_id),
  });
};

export const useUpdateClientEvent = (clientId: string | null) => {
  const invalidate = useInvalidateClientEvents();
  return useMutation({
    mutationFn: (args: { id: string; input: UpdateClientEventInput }) =>
      clientEventsApi.update(args.id, args.input),
    onSuccess: () => invalidate(clientId),
  });
};

export const useDeleteClientEvent = (clientId: string | null) => {
  const invalidate = useInvalidateClientEvents();
  return useMutation({
    mutationFn: (id: string) => clientEventsApi.remove(id),
    onSuccess: () => invalidate(clientId),
  });
};
